// @ts-strict-ignore
import { BaseSingleConnectionConnectProcessor } from './base-single-connection-connect.processor';
import { AcceptingConnectionSortGroupHelper } from '@3d-scene/helper/connection/accepting-connection-sort-group.helper';
import { ArticleUsageVO, ArticleVO } from '@article/model/vo/article.vo';
import { ConnectArticlePrepareData } from '../insert/connect-article-prepare-data';
import { ArticleContainersWrapper } from '@3d-scene/model3d/article-containers-wrapper';
import { Connection } from '@3d-scene/model3d/connection';
import { ConnectionAreaUsageVO, ConnectionAreaVO, MetaElementUsageIdentityVO, MetaElementUsageVO, MetaElementVO } from '@shared/vo/meta-element.vo';
import { Rectangle3D } from '@3d-scene/model/rectangle-3d';
import { ArticleContainerPropertyDef } from '@article/constants/article-container-property-def';
import { ArticleTypeConstants } from '@article/constants/article-type-constants';
import { Matrix4, Vector3 } from 'three';
import { ArticleUtils } from '@article/util/article-utils';
import { PointsPair } from '@3d-scene/model/points-pair';
import { Line } from '@3d-scene/model/line';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { MathUtils } from '@3d-scene/common/util/math-utils';
import { ArticleInfoVO } from '@project/model/vo/project.vo';
import { ArticleContainerUtils } from '@3d-scene/model3d/util/article-container-utils';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { CompositionContainer } from '@3d-scene/model3d/composition-container';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { ArticleContainerFactory } from '@3d-scene/model3d/article-container.factory';
import { ArticleManager } from '@article/manager/article.manager';
import { CompositeArticleCacheManager } from '@article/manager/composite-article-cache.manager';
import { ArticleContainerLoader } from '@article/service/article-container-loader';
import { ProjectModel } from '@project/model/project.model';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { CompositeArticleUtils } from '@article/util/composite-article-utils';
import { ArticleChangedService } from '@3d-scene/service/article-changed.service';
import { ProjectModelService } from '@project/service/project-model.service';
import { ProjectArticleService } from '@project/service/project-article.service';
import { ProjectMetaElementService } from '@project/manager/project-meta-element.service';
import { ConnectionInfoUtils } from '@3d-scene/model3d/util/connection-info-utils';
import { DetachArticleUsageService } from '@project/service/detach-article-usage.service';
import { ArticleContainerService } from '@3d-scene/service/article-container.service';
import { ConnectionAreaUsageUtils } from '@shared/util/connection-area-usage-utils';
import { CompositeArticleInfoUtils } from '@article/util/composite-article-info-utils';
import { Object3DUtils } from '@3d-scene/common/util/object-3d-utils';

export class BaseDynamicArticleConnectProcessor extends BaseSingleConnectionConnectProcessor {
  protected _isInversedDirection: boolean = false;

  constructor(
    projectModel: ProjectModel,
    projectModelService: ProjectModelService,
    projectArticleService: ProjectArticleService,
    protected projectMetaElementService: ProjectMetaElementService,
    articleContainerLoader: ArticleContainerLoader,
    compositeArticleCacheManager: CompositeArticleCacheManager,
    articleContainerFactory: ArticleContainerFactory,
    articleManager: ArticleManager,
    compositeArticleUtils: CompositeArticleUtils,
    compositeArticleInfoUtils: CompositeArticleInfoUtils,
    articleContainerUtils: ArticleContainerUtils,
    protected localeResourceManager: LocaleResourceManager,
    protected acceptingConnectionSortGroupHelper: AcceptingConnectionSortGroupHelper,
    containerService: ContainerService,
    protected _articleChangedService: ArticleChangedService,
    detachArticleUsageService: DetachArticleUsageService,
    private articleContainerService: ArticleContainerService,
  ) {
    super(
      projectModel,
      projectModelService,
      projectArticleService,
      articleContainerLoader,
      compositeArticleCacheManager,
      articleContainerFactory,
      articleManager,
      compositeArticleUtils,
      compositeArticleInfoUtils,
      articleContainerUtils,
      detachArticleUsageService,
      containerService,
    );
  }

  protected internalPreProcess(article: ArticleVO, connectPrepareData: ConnectArticlePrepareData, handler: Function): void {
    const articleWrappers: ArticleContainersWrapper[] = this.preProcessWithoutRefresh(
      article,
      connectPrepareData.acceptingConnections,
      connectPrepareData.vectorId,
      connectPrepareData.givingConnectionNames
    );
    handler(articleWrappers);
  }

  public preProcessWithoutRefresh(
    article: ArticleVO,
    connectionCubes: Connection[],
    searchVectorId: number = NaN,
    givingConnectionNames: string[] = null
  ): ArticleContainersWrapper[] {
    const originalDynamicMetaElements: MetaElementVO[] = [];
    for (const metaElement of article.metaElements) {
      if (!ConnectionInfoUtils.isStaticMetaElement(metaElement)) {
        originalDynamicMetaElements.push(metaElement);
      }
    }
    const sortedConnections: Connection[] = ConnectionInfoUtils.sortConnectionsByPoint(connectionCubes);
    // divide connectionCubes by groups
    const articleWrappers: ArticleContainersWrapper[] = [];
    const connectionGroups: Connection[][] = this.createAcceptingConnectionGroups(sortedConnections);
    for (const connectionGroup of connectionGroups) {
      const rectangle: Rectangle3D = this.calculateAcceptingRectangleByConnections(connectionGroup);
      if (!this.isConnectionGroupValid(connectionGroup, rectangle, article)) {
        continue;
      }
      const articleWrapper: ArticleContainersWrapper = this.createArticleWrapperByConnectionGroup(
        article,
        connectionGroup,
        rectangle,
        searchVectorId,
        givingConnectionNames
      );
      articleWrapper.properties.setValue(ArticleContainerPropertyDef.ORIGINAL_META_ELEMENTS, originalDynamicMetaElements);
      articleWrappers.push(articleWrapper);
    }
    return articleWrappers;
  }

  protected isConnectionGroupValid(connectionGroup: Connection[], acceptingRectangle: Rectangle3D, article: ArticleVO): boolean {
    return true;
  }

  protected createArticleWrapperByConnectionGroup(article: ArticleVO, connectionGroup: Connection[], acceptingRectangle: Rectangle3D,
    searchVectorId: number, givingConnectionNames: string[]): ArticleContainersWrapper {
    const articleWrapper: ArticleContainersWrapper = new ArticleContainersWrapper(
      article,
      null,
      ArticleTypeConstants.EXCLUDED_TYPES_FROM_COMPOSITE_ARTICLE,
      this.projectModel,
      this.projectArticleService,
      this.articleContainerLoader,
      this.compositeArticleCacheManager,
      this.articleContainerFactory,
      this.articleManager,
      this.articleContainerUtils,
      this.detachArticleUsageService
    );
    articleWrapper.properties.setValue(ArticleContainerPropertyDef.RECTANGLE, acceptingRectangle);
    articleWrapper.properties.setValue(ArticleContainerPropertyDef.STRETCH_LENGTH, this.getStretchLengthByAcceptingRectangle(acceptingRectangle));
    articleWrapper.properties.setValue(ArticleContainerPropertyDef.ACCEPTING_CONNECTIONS, connectionGroup);
    articleWrapper.searchVectorId = searchVectorId;
    articleWrapper.givingConnectionNames = givingConnectionNames;
    return articleWrapper;
  }

  protected getStretchLengthByAcceptingRectangle(acceptingRectangle: Rectangle3D): number {
    if (!acceptingRectangle) {
      return NaN;
    }
    return acceptingRectangle.point1.distanceTo(acceptingRectangle.point4);
  }

  protected createAcceptingConnectionGroups(sortedConnections: Connection[]): Connection[][] {
    return this.acceptingConnectionSortGroupHelper.splitConnectionCubesOnGroups(
      sortedConnections,
      false,
      this.ignoreDistanceBetweenAcceptingConnections
    );
  }

  protected get ignoreDistanceBetweenAcceptingConnections(): boolean {
    return false;
  }

  public process(
    containers: ArticleContainer[],
    baseContainer: ArticleContainer,
    givingConnectionAreas: ConnectionAreaVO[],
    rotationAngle: number,
    updateTextures: boolean = true
  ): void {
    const acceptingConnectionCubes: Connection[] = baseContainer.properties.getValue(ArticleContainerPropertyDef.ACCEPTING_CONNECTIONS);
    const originalDynamicMetaElements: MetaElementVO[] = baseContainer.properties.getValue(ArticleContainerPropertyDef.ORIGINAL_META_ELEMENTS);
    const givingConnection: ConnectionAreaVO = givingConnectionAreas[0];

    if (acceptingConnectionCubes && acceptingConnectionCubes.length > 0 && originalDynamicMetaElements) {
      // get connector cube for giving area
      const givingCube: Connection = baseContainer.findFirstConnectionByName(givingConnection.name);
      // get accepting area connection free space rectangle
      const acceptingRectangle: Rectangle3D = this.getAcceptingRectangle(baseContainer);
      if (givingCube && givingCube.compositionRectangle && acceptingRectangle) {
        const givingRectangle: Rectangle3D = givingCube.compositionRectangle;
        const rotMatrix: Matrix4 = MathUtils.getRotationMatrix(givingRectangle.toFace(), acceptingRectangle.toFace(), true, NaN, NaN, true);
        baseContainer.applyMatrix4(rotMatrix);
        // giving u-direction side
        let givingSideMiddlePoint: Vector3 = MathUtils.calculateMiddlePoint(givingRectangle.point1, givingRectangle.point4);
        // accepting v-direction side
        const acceptingSideMiddlePoint: Vector3 = MathUtils.calculateMiddlePoint(acceptingRectangle.point1, acceptingRectangle.point2);
        givingSideMiddlePoint = MathUtils.rotateVector(givingSideMiddlePoint, rotMatrix);
        givingSideMiddlePoint = acceptingSideMiddlePoint.clone().sub(givingSideMiddlePoint);
        baseContainer.moveTo(givingSideMiddlePoint.x, givingSideMiddlePoint.y, givingSideMiddlePoint.z);

        this.finalizeProcess(baseContainer, acceptingConnectionCubes, originalDynamicMetaElements, updateTextures);
      } else {
        throw new Error(this.localeResourceManager.getValueByKey('connectProcessor.message.unknown_connection', [givingConnection.name]));
      }
    } else {
      throw new Error(this.localeResourceManager.getValueByKey('connectProcessor.message.no_connection'));
    }
  }

  private getAcceptingRectangle(dynamicArticleContainer: ArticleContainer): Rectangle3D {
    let rectangle: Rectangle3D = dynamicArticleContainer.properties.getValue(ArticleContainerPropertyDef.RECTANGLE);
    if (!rectangle) {
      return null;
    }
    if (this.shouldSwitchPoints(rectangle, dynamicArticleContainer)) {
      rectangle = this.switchPoints(rectangle);
    }
    return rectangle;
  }

  private switchPoints(baseRectangle: Rectangle3D): Rectangle3D {
    const rectangle: Rectangle3D = new Rectangle3D();
    rectangle.point1 = baseRectangle.point3;
    rectangle.point4 = baseRectangle.point2;
    rectangle.point3 = baseRectangle.point1;
    rectangle.point2 = baseRectangle.point4;
    return rectangle;
  }

  protected shouldSwitchPoints(rectangle: Rectangle3D, dynamicArticleContainer: ArticleContainer): boolean {
    return false;
  }

  protected finalizeProcess(
    baseContainer: ArticleContainer,
    acceptingConnectionCubes: Connection[],
    originalDynamicMetaElements: MetaElementVO[],
    updateTextures: boolean
  ): void {
    this.connectArticleWithParent(baseContainer, acceptingConnectionCubes);
    // remove old article info if it exists
    this.projectModel.removeArticleInfoFromCurrentProject(baseContainer.articleGUID);
    const articleInfo: ArticleInfoVO = this.articleContainerService.createDynamicArticleInfo(baseContainer, originalDynamicMetaElements);
    this.projectModel.currentProject.dynamicArticlesInfo.push(articleInfo);
    this._articleChangedService.articleChanged(baseContainer.articleGUID, updateTextures);
  }

  private connectArticleWithParent(givingArticleContainer: ArticleContainer, acceptingConnectionCubes: Connection[]): void {
    const firstAcceptingContainer: ArticleContainer = this.containerService.getArticleContainer(acceptingConnectionCubes[0]);
    const compositionContainer: CompositionContainer = this.containerService.getCompositionContainer(firstAcceptingContainer);
    let acceptingIdentities: MetaElementUsageIdentityVO[] = this.updateAcceptingUsages(givingArticleContainer.articleUsage);
    if (acceptingIdentities.length === 0) {
      acceptingIdentities = this.createMetaElementUsages(givingArticleContainer, acceptingConnectionCubes);
    }
    this.finalizeAttachContainer(compositionContainer.compositionGuid, givingArticleContainer, acceptingIdentities);
  }

  private updateAcceptingUsages(childArticleUsage: ArticleUsageVO): MetaElementUsageIdentityVO[] {
    const acceptingIds: MetaElementUsageIdentityVO[] = [];
    for (const givingConnectionUsage of ArticleUsageUtils.backwardConnectionAreaUsages(childArticleUsage)) {
      const acceptingIdentity: MetaElementUsageIdentityVO = givingConnectionUsage.connection.acceptingAreaId;
      const acceptingMetaElement: MetaElementVO = this.findMetaElementByIdentity(acceptingIdentity);
      if (acceptingMetaElement) {
        let acceptingConnectionUsage: ConnectionAreaUsageVO = this.projectMetaElementService.getConnectionAreaUsage(
          acceptingIdentity.articleUsageId,
          acceptingIdentity.metaElementName
        );
        if (!acceptingConnectionUsage) {
          acceptingConnectionUsage = this.projectMetaElementService.addMetaElementUsage(
            acceptingIdentity.articleUsageId,
            acceptingMetaElement
          ) as ConnectionAreaUsageVO;
        }
        ConnectionAreaUsageUtils.connect(acceptingConnectionUsage, givingConnectionUsage);
        acceptingIds.push(acceptingIdentity);
      }
    }
    return acceptingIds;
  }

  private createMetaElementUsages(givingArticleContainer: ArticleContainer, acceptingConnectionCubes: Connection[]): MetaElementUsageIdentityVO[] {
    const acceptingIdentities: MetaElementUsageIdentityVO[] = [];
    const connectionAreas: ConnectionAreaVO[] = ArticleUtils.getGivingConnectionsInActiveSet(
      givingArticleContainer.article,
      givingArticleContainer.articleUsage
    );
    if (this._isInversedDirection) {
      acceptingConnectionCubes.reverse();
    }

    for (let i: number = 0; i < acceptingConnectionCubes.length; i++) {
      const acceptingCube: Connection = acceptingConnectionCubes[i];
      const acceptingArticleContainer: ArticleContainer = this.containerService.getArticleContainer(acceptingCube);

      if (!acceptingCube.metaElementUsage) {
        acceptingCube.metaElementUsage = this.projectMetaElementService.addMetaElementUsage(acceptingArticleContainer.articleGUID, acceptingCube.metaElementVO);
      }
      const acceptingArea: ConnectionAreaUsageVO = acceptingCube.metaElementUsage as ConnectionAreaUsageVO;

      const givingMetaElementUsage: MetaElementUsageVO = this.projectMetaElementService.addMetaElementUsage(
        givingArticleContainer.articleGUID,
        connectionAreas[i]
      );
      const givingArea: ConnectionAreaUsageVO = givingMetaElementUsage as ConnectionAreaUsageVO;

      ConnectionAreaUsageUtils.connect(acceptingArea, givingArea);
      acceptingIdentities.push(acceptingArea.identity);
    }
    return acceptingIdentities;
  }

  private findMetaElementByIdentity(metaUsageId: MetaElementUsageIdentityVO): MetaElementVO {
    if (!metaUsageId) {
      return null;
    }
    const articleContainer: ArticleContainer = this.containerService.findArticleContainerInGlobal(metaUsageId.articleUsageId);
    if (!articleContainer) {
      return null;
    }
    return ArticleUtils.findMetaElementByName(articleContainer.article, metaUsageId.metaElementName);
  }

  protected calculateConnectionRectangle(connectionCube: Connection): Rectangle3D {
    return connectionCube.compositionRectangle;
  }

  protected calculateAcceptingRectangleByConnections(sortedConnections: Connection[]): Rectangle3D {
    if (sortedConnections.length === 1) {
      return this.calculateConnectionRectangle(sortedConnections[0]);
    }
    const closestConnectionCube: Connection = sortedConnections[0];
    const farestConnectionCube: Connection = sortedConnections[sortedConnections.length - 1];
    return this.calculateAcceptingRectangleByStartEndConnection(closestConnectionCube, farestConnectionCube, sortedConnections);
  }

  protected calculateAcceptingRectangleByStartEndConnection(
    closestConnectionCube: Connection,
    farestConnectionCube: Connection,
    connections: Connection[]
  ): Rectangle3D {
    const closestRectangle: Rectangle3D = this.calculateConnectionRectangle(closestConnectionCube);
    const farestRectangle: Rectangle3D = this.calculateConnectionRectangle(farestConnectionCube);
    return this.calculateAcceptingRectangleByStartEndRectangle(closestRectangle, farestRectangle, connections);
  }

  protected calculateAcceptingRectangleByStartEndRectangle(
    closestRectangle: Rectangle3D,
    farestRectangle: Rectangle3D,
    connections: Connection[]
  ): Rectangle3D {
    const closestPoints: Vector3[] = closestRectangle.toArray();
    const farestPoints: Vector3[] = farestRectangle.toArray();

    const pointPairs: PointsPair[] = Object3DUtils.findPointPairs(closestPoints, farestPoints);

    // If the rectangles are identical, return the closest rectangle.
    if (MathUtils.areRectanglesCongruent(closestRectangle, farestRectangle)) {
      return closestRectangle.clone();
    }

    /* Point pairs is a pair of points, which resides on different connections/articles, for example
     *
     * |a1   a4|  |b1   b4|
     * |       |  |       |
     * |a2   a3|  |b2   b3|
     *
     * The farest pairs will be (a1, b3) and (a2, b4). And always first point in pair will be point on
     * the first article, and second point - point of the second article.
     */
    const pair1: PointsPair = pointPairs[0];
    const pair2: PointsPair = pointPairs[1];

    /*
     * Rectangle sides are left and right rectangle borders: (a1, a2) and (b3, b4)
     */
    const line1: Line = ConnectionInfoUtils.calculateRectangleSide(pair2.startPoint, pair1.startPoint, connections);
    const line2: Line = ConnectionInfoUtils.calculateRectangleSide(pair2.endPoint, pair1.endPoint, connections);

    return Rectangle3D.fromArray(this.constructRectanglePoints(line1, line2, this.calculateConstantPoint(connections[0])));
  }

  private constructRectanglePoints(line1: Line, line2: Line, constantPoint: Vector3): Vector3[] {
    const points: Vector3[] = [line1.start, line1.end, line2.start, line2.end];

    let closest: Vector3 = points[0]; // point#1
    let closestDistance: number = constantPoint.distanceTo(closest);
    let farest: Vector3 = points[0]; // point#3
    let farestDistance: number = constantPoint.distanceTo(farest);

    for (let index: number = 1; index < points.length; index++) {
      const currentDistance: number = constantPoint.distanceTo(points[index]);
      if (currentDistance < closestDistance) {
        closest = points[index];
        closestDistance = currentDistance;
      }
      if (currentDistance > farestDistance) {
        farest = points[index];
        farestDistance = currentDistance;
      }
    }

    let closestNeighbor: Vector3; // point#2
    let farestNeighbor: Vector3; // point#4
    if (line1.hasPoint(closest)) {
      closestNeighbor = line1.start === closest ? line1.end : line1.start;
      farestNeighbor = line2.start === farest ? line2.end : line2.start;
    } else {
      closestNeighbor = line2.start === closest ? line2.end : line2.start;
      farestNeighbor = line1.start === farest ? line1.end : line1.start;
    }
    return [closest, closestNeighbor, farest, farestNeighbor];
  }

  private calculateConstantPoint(cube: Connection): Vector3 {
    const rect: Rectangle3D = cube.compositionRectangle;
    return MathUtils.getInterpolated(rect.point1, rect.point3, 10000 / rect.point1.distanceTo(rect.point3));
  }

  public clear(): void {
    super.clear();
    this._isInversedDirection = false;
  }
}
